"use client";

import { useQuery } from "@tanstack/react-query";
import { useCentraClient, useSelection } from "@trydig/centra/client";
import { formatCartItems } from "@trydig/centra/formatting";

export type CartItem = NonNullable<ReturnType<typeof formatCartItems>>[number];

// Product item ID for the Stine Sofie Stiftelsen donation product
// (https://localhost:3000/en-NO/product/stine-sofie-stiftelsen). It's a single
// fixed donation product with no size/colour variants, so it's safe to hardcode.
// It's a real Centra selection line, but shouldn't be displayed like a normal
// product — it's filtered out of `items`/`count` here and surfaced separately
// as `donation`, so every screen that lists cart items or counts them stays
// consistent automatically.
export const DONATION_ITEM_ID = "78132-45924";

// The donation's own (base/display) product ID — distinct from the item
// variant ID above. `GET /products/{product}` (unlike the /products *search*
// endpoint, which never lists this product at all — it's unlisted/hidden from
// catalog browsing by design) only accepts this form of ID.
const DONATION_PRODUCT_ID = "78132";

export const useCart = () => {
	return useSelection({
		select: (data) => {
			// Cart thumbs render at ~117x156 — `standard` (600x800) is plenty for
			// retina and saves a lot vs `full` (972x972 square, also wrong aspect).
			const allItems = formatCartItems(data, "standard");
			const items = allItems?.filter((item) => item.item !== DONATION_ITEM_ID);
			const donation = allItems?.find((item) => item.item === DONATION_ITEM_ID);
			const count = items?.reduce((n, item) => n + (item.quantity ?? 0), 0) ?? 0;

			return {
				items,
				donation,
				count,
				country: data.location?.country,
				currency: data.selection?.currency,
				subtotal: data.selection?.totals?.itemsTotalPrice,
				subtotalAsNumber: data.selection?.totals?.itemsTotalPriceAsNumber,
				shippingPrice: data.selection?.totals?.shippingPrice,
				shippingPriceAsNumber: data.selection?.totals?.shippingPriceAsNumber,
				total: data.selection?.totals?.grandTotalPrice,
				vat: data.selection?.totals?.grandTotalPriceTax,
				// Based on real products only — the donation alone shouldn't count as
				// "has items" anywhere (cart drawer, checkout access guard, etc.), or a
				// donation-only selection would present as a normal, checkout-able cart.
				hasItems: (items?.length ?? 0) > 0,
				vouchers: data?.selection?.discounts?.vouchers,
			};
		},
	});
};

/**
 * Whether the donation product is available to buy in the shopper's current
 * market — the exact same per-market "display" control Centra applies to
 * every other product, not a hardcoded market list. A product not displayed
 * in the current market simply doesn't resolve (`GET /products/{product}`
 * 404s), same as it wouldn't show up on a category page or resolve its own
 * PDP there.
 */
export const useDonationAvailability = () => {
	const client = useCentraClient();
	const { data } = useCart();
	const country = data?.country;

	return useQuery({
		// Re-check whenever the resolved market/country changes (e.g. the
		// shopper switches country), not just once per session.
		queryKey: ["donation-availability", DONATION_PRODUCT_ID, country],
		queryFn: async () => {
			const { data, error } = await client.GET("/products/{product}", {
				params: { path: { product: DONATION_PRODUCT_ID } },
			});
			if (error) return false;
			return data.product?.available === true;
		},
		enabled: !!country,
	});
};
